import { TFunction as OriginalTFunction } from 'i18next';
import { createElement, Fragment, ReactElement } from 'react';
import reactStringReplace from 'react-string-replace';

import { TFunction } from '@shared/localization';

import {
  isTFunctionValue,
  TFunctionComponent,
  TFunctionValue,
} from './TFunction';

/**
 * Builds a {@link TFunction} bound to a given i18next translate function.
 * Extracted from the hook so it can be unit tested in isolation by passing a
 * mock translate function.
 */
export function createTranslator(originalT: OriginalTFunction): TFunction {
  function customT(key: string): string;
  function customT(key: string, values: Record<string, TFunctionValue>): string;
  function customT(
    key: string,
    values: Record<string, TFunctionComponent>,
  ): ReactElement[];
  function customT(
    key: string,
    values: Record<string, TFunctionValue | TFunctionComponent>,
  ): ReactElement[];
  function customT(
    key: string,
    values?: Record<string, TFunctionValue | TFunctionComponent>,
  ): string | ReactElement[] {
    const options = splitByValueType(values || {});

    const translation = originalT(key, options.values);

    const parts = Object.keys(options.components).reduce(
      (acc: string | ReactElement[], component): string | ReactElement[] => {
        const componentPlaceholder = `{{${component}}}`;
        if (!translation.includes(componentPlaceholder)) {
          console.warn(
            `Translation for key "${key}" does not contain component placeholder "${component}".`,
          );

          return acc as string | ReactElement[];
        }

        return replaceStringWithComponent(
          translation,
          componentPlaceholder,
          (component) => {
            const componentName = component.replace(/{{|}}/g, '');
            return options?.components?.[componentName] || null;
          },
        );
      },
      translation,
    );

    if (typeof parts === 'string') {
      return parts;
    } else {
      return parts.map((elem, index) =>
        createElement(Fragment, { key: index }, elem),
      );
    }
  }

  return customT;
}

function replaceStringWithComponent(
  source: string,
  replace: string,
  replacementCallback: (match: string, index?: number) => ReactElement,
): ReactElement[] {
  return reactStringReplace(
    source,
    replace,
    replacementCallback,
  ) as ReactElement[];
}

function splitByValueType(
  input: Record<string, TFunctionValue | TFunctionComponent>,
) {
  const values: Record<string, TFunctionValue> = {};
  const components: Record<string, TFunctionComponent> = {};

  for (const [key, value] of Object.entries(input)) {
    if (isTFunctionValue(value)) {
      values[key] = value;
    } else {
      components[key] = value;
    }
  }

  return { values, components };
}
